// Include phoenix_html to handle method=PUT/DELETE in forms and buttons.
import "phoenix_html";
// Establish Phoenix Socket and LiveView configuration.
import { Socket } from "phoenix";
import { LiveSocket } from "phoenix_live_view";
import topbar from "../vendor/topbar";
import { PriceChartTooltip } from "./hooks/price_chart_tooltip";
import { FlashMessage } from "./hooks/flash_message";
import { RecentlyViewed } from "./hooks/recently_viewed";
import { EChartsHook } from "./hooks/echarts_hook";
import { ChatPing } from "./hooks/chat_ping";
import { HeaderSearchReveal } from "./hooks/header_search_reveal";
import { WebMcpTools } from "./hooks/webmcp_tools";
import { CopyText } from "./hooks/copy_text";

// Prevent duplicate topbar instances (memory leak fix)
window.topbar = window.topbar || topbar;

let csrfToken = document
  .querySelector("meta[name='csrf-token']")
  .getAttribute("content");

let cspNonce = document
  .querySelector("meta[name='csp-nonce']")
  .getAttribute("content");

// The WebGL renderer, or "" without WebGL. Headless Chrome renders in
// software ("SwiftShader"), and so do the fleets: BotDetector flags it.
function webglRenderer() {
  try {
    const gl = document.createElement("canvas").getContext("webgl");
    const ext = gl && gl.getExtension("WEBGL_debug_renderer_info");
    return gl ? String(gl.getParameter(ext ? ext.UNMASKED_RENDERER_WEBGL : gl.RENDERER) || "") : "";
  } catch (_e) {
    return "";
  }
}

// Browser signals for bot identification (spec 2026-09-29 §3.2). The server
// compares them with the request and records the visit's proof.
const botSignals = {
  wd: navigator.webdriver === true,
  ua: navigator.userAgent,
  lang: (navigator.languages || []).length,
  w: window.outerWidth,
  h: window.outerHeight,
  gl: webglRenderer(),
};

let liveSocket = new LiveSocket("/live", Socket, {
  params: { _csrf_token: csrfToken, _csp_nonce: cspNonce, _b: botSignals },
  hooks: { PriceChartTooltip, FlashMessage, RecentlyViewed, EChartsHook, ChatPing, HeaderSearchReveal, WebMcpTools, CopyText },
  longPollFallbackMs: 10000,
  reconnectAfterMs: function(tries) {
    return [10, 50, 100, 150, 200, 250, 500, 1000, 2000][tries - 1] || 5000
  },
  dom: {
    onBeforeElUpdated(from, to) {
      if (from.nodeName === "SCRIPT" && to.hasAttribute && to.hasAttribute("nonce")) {
        from.setAttribute("nonce", to.getAttribute("nonce"));
      }
      if (from.nodeName === "STYLE" && to.hasAttribute && to.hasAttribute("nonce")) {
        from.setAttribute("nonce", to.getAttribute("nonce"));
      }
    }
  }
});

// Show progress bar on live navigation and form submits (delayed for better UX)
window.topbar.config({ barColors: { 0: "#FFFFFF" }, shadowColor: "rgba(255, 255, 255, .3)" });
let topBarScheduled = undefined;
window.addEventListener("phx:page-loading-start", () => {
  if (!topBarScheduled) {
    topBarScheduled = setTimeout(() => window.topbar.show(), 120);
  }
});
window.addEventListener("phx:page-loading-stop", () => {
  clearTimeout(topBarScheduled);
  topBarScheduled = undefined;
  window.topbar.hide();
});

// Defer connect until the render-blocking stylesheet has loaded: LiveView's
// first geometry reads then see styled layout (silencing Firefox's forced
// layout/FOUC advisory) without waiting for images the way window "load"
// would. Warm cache: the sheet is already applied and we connect at once.
const appCss = document.querySelector('link[rel="stylesheet"][href*="/assets/app"]');
const connectLiveSocket = () => liveSocket.connect();
if (appCss && !appCss.sheet) {
  appCss.addEventListener("load", connectLiveSocket, { once: true });
  // Never leave the page dead if the stylesheet fetch fails.
  appCss.addEventListener("error", connectLiveSocket, { once: true });
} else {
  setTimeout(connectLiveSocket, 0);
}

// Handle bfcache (back-forward cache) restoration
// When browser restores page from bfcache, WebSocket connection is stale
window.addEventListener("pageshow", (event) => {
  if (event.persisted) {
    liveSocket.connect();
  }
});

// expose liveSocket on window for web console debug logs and latency simulation:
// >> liveSocket.enableDebug()
// >> liveSocket.enableLatencySim(1000)  // enabled for duration of browser session
// >> liveSocket.disableLatencySim()
window.liveSocket = liveSocket;

// Domain selector flag updates
// Only runs if domain-select elements are present on the page
function initDomainSelectors() {
  const selects = document.querySelectorAll('.domain-select');
  if (selects.length === 0) return; // Early exit if not needed

  // Get flag paths from first select element's data attribute
  const firstSelect = selects[0];
  const flagPathsData = firstSelect.closest('form')?.dataset?.flagPaths;

  if (!flagPathsData) return;

  const flagPaths = JSON.parse(flagPathsData);

  function updateDomainFlag(select) {
    const option = select.options[select.selectedIndex];
    const flagId = select.dataset.flagTarget || 'domain-flag';
    const flag = document.getElementById(flagId);
    if (flag && option.dataset.flag) {
      flag.src = flagPaths[option.dataset.flag] || option.dataset.flag;
    }
  }

  // Add listeners to all domain select elements
  selects.forEach(function(select) {
    if (!select.hasAttribute('data-listener-added')) {
      select.setAttribute('data-listener-added', 'true');
      select.addEventListener('change', function() {
        updateDomainFlag(this);
      });
    }
  });
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', initDomainSelectors);

// Re-initialize after LiveView updates
document.addEventListener('phx:page-loading-stop', initDomainSelectors);
